import { Component, OnInit, ChangeDetectorRef, ViewChildren, ViewChild, QueryList } from '@angular/core';
import * as _ from 'lodash';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormArray, AbstractControl, ValidatorFn } from '@angular/forms';

// data, models, helpers
import { segmentAnimation } from '../base/animations';
import { descriptionSegmentData } from '../../segment-data/description-segment-data';
import { getAvailableLanguages } from 'utils/utils';
import { DescriptionModel } from '../../models/description.model';
import { fieldGroups, halfSizeFields } from './fields-info';
import { SegmentFieldItem, ValidationItem } from 'utils/segment-field-item.interface';

// components
import { BaseSegmentComponent } from '../base/_base-segment.component';
import { DetailsControlComponent } from 'shared/details-page/details-control/details-control.component';
import { AppSummaryModalComponent } from '../app-summary-modal/app-summary-modal.component';
import { SimpleConfirmModalComponent } from 'shared/modal/simple-confirm-modal/simple-confirm-modal.component';

// services
import { BrandService } from 'shared/brand/brand.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';


@Component({
  moduleId: module.id,
  selector: 'description-segment',
  templateUrl: 'description.component.html',
  styleUrls: ['../base/_base-segment.component.scss', 'description.component.scss'],
  animations: [segmentAnimation],
})
export class DescriptionComponent extends BaseSegmentComponent implements OnInit {

  @ViewChildren('loopingControl')
  loopingControls: QueryList<DetailsControlComponent>;

  /*
   * The Modal used to manage the English version of the Application Summary
   */
  @ViewChild(AppSummaryModalComponent, {static: false})
  appSummaryModal: AppSummaryModalComponent | void;

  /*
   * The Confirmation Modal used when the App Summary Modal is completed.
   */
  @ViewChild('confirmAppSummaryChangeModal', {static: false})
  confirmAppSummaryChangeModal: SimpleConfirmModalComponent | void;

  segmentName: string = 'descriptions';
  declare segmentForm: UntypedFormGroup;
  segmentData: Array<SegmentFieldItem> = descriptionSegmentData;
  descriptionModel: DescriptionModel;
  pathToScores: string = 'segments.descriptions.tallies';
  // grouped data, used to render dom
  fieldGroups: Array<any> = [];

  selectedLanguageItem: any;
  languageItems: Array<any> = [];
  loopingFields: Array<string> = ['sho', 'inv', 'des', 'ext',
    'asc', 'mkt', 'fab',
    'abr', 'lab', 'key',
    'shp', 'sla', 'uns', 'vmr', 'cap', 'tra', 'mtp', 'tle', 'ttp', 'ttd'];

  /*
   * appSummaryData has 2 fields
   *  appSummary is the current App Summary for the item detail record
   *  selectedAppSummary is the app summary selected from the App Summary Modal
   */
  appSummaryData: any = {
    appSummary: {
      template_id: null,
      custom_value: ['']
    },
    selectedAppSummary: null
  };

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get selectedLanguageLower(): string {
    return _.toLower(this.selectedLanguage);
  }

  get selectedLanguage(): string {
    return _.get(this.selectedLanguageItem, 'code', 'EN');
  }

  /*
   * Get the Application Summary text from the components form
   */
  get appSummary() {
    return this.segmentForm.get('en').get('asm').value;
  }

  /*
   * Get the Application Summary text from the components form
   */
  get appSummaryTemplateId() {
    return this.segmentForm.get('appSummaryTemplateId').value;
  }

  get appSummaryMaxLength(): number {
    const asmItem: SegmentFieldItem = _.find(this.segmentData, {item_name: 'descriptions.ASM'});
    const rule: ValidationItem = asmItem.validation[this.scorecardData?.validation_rule];
    return rule ? rule.maxLength : 2000;
  }

  /*******************************************
  * COMPONENT SETTERS
  *******************************************/
  /*
   * Set the English version of the Application summary in the Components form
   */
  set appSummary(appSummary: string | Array<string>) {
    const asmItem: SegmentFieldItem = _.find(this.segmentData, {item_name: 'descriptions.ASM'});
    const validators: Array<ValidatorFn> =  this.getFieldValidators(asmItem);
    const formGroup: any = this.segmentForm.controls['en'];
    let formArray: UntypedFormArray = formGroup.get('asm');
    if (formArray.length) {
      formGroup.setControl('asm', new UntypedFormArray([]));
      formArray = formGroup.get('asm');
    }
    _.each(appSummary, (desc) => {
      formArray.push(new UntypedFormControl(desc,  validators));
    });
  }
  /*
   * Set the Application Summary Template Id in the Components form
   */
  set appSummaryTemplateId(appSummaryTemplateId: number) {
    this.segmentForm.get('appSummaryTemplateId').setValue(appSummaryTemplateId);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.languageItems = getAvailableLanguages();

    /*
      Initialize form
    */
    this.segmentForm = this.fb.group({});
    this.segmentForm = this.fb.group({
      en: this.fb.group(this.languageFormGroup(this.segmentData)),
      es: this.fb.group(this.languageFormGroup(this.segmentData)),
      fr: this.fb.group(this.languageFormGroup(this.segmentData)),
    });
    if (this.itemDetail) { this.handleAllDataUpdate(); }
    this.separateSegmentFields(this.segmentData);

    _.defer(() => {
      this.runAnimation('show');
    });
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onLanguageChange(event: any) {
    this.selectedLanguageItem = event;
    this.ref.markForCheck();
  }

  onAddAnotherClick(item: SegmentFieldItem, language: string) {
    const validators: any = this.getFieldValidators(item);

    const formArray: any = this.getLoopingformArray(item.field_map, language);
    formArray.push(new UntypedFormControl('',  validators));
    this.scorecardConnectorService.controlAdded.emit({
      item, index: formArray.length - 1, language: this.selectedLanguage,
    });
    _.defer(() => {
      this.focusOnLastLoopingControl(item.field_map);
      this.markAsDirty();
    });
  }

  onDeleteControlClick(control: UntypedFormControl, item: SegmentFieldItem, language: string) {
    const formArray: any = this.getLoopingformArray(item.field_map, language);
    if (!formArray) { return console.warn('formArray not found!'); }
    const index = formArray.controls.indexOf(control);
    formArray.removeAt(index);
    this.scorecardConnectorService.controlRemoved.emit({
      item, index, language: this.selectedLanguage,
    });
    this.markAsDirty();
  }

  onLoopingControlFocus(item: SegmentFieldItem, index: number) {
    this.scorecardConnectorService.controlFocused.emit({
      item, index, language: this.selectedLanguage
    });
  }

  onLoopingControlStatusChange(status: any, item: SegmentFieldItem, index: number) {
    this.scorecardConnectorService.controlStatusChanged.emit({
      status, item, index, language: this.selectedLanguage
    });
  }

  onLangControlStatusChange(status: any, item: any) {
    this.scorecardConnectorService.controlStatusChanged.emit({
      status, item, language: this.selectedLanguage
    });
  }

  onLangControlFocus(item: any) {
    this.scorecardConnectorService.controlFocused.emit({
      item, language: this.selectedLanguage
    });
  }

  onTextPopulatedChange(populated: number, item: SegmentFieldItem, index: number) {
    this.scorecardConnectorService.controlPopulatedChanged.emit({
      populated, item, index, language: this.selectedLanguage
    });
  }

  /*******************************************
  * APP SUMMARY MODAL HANDLERS
  *******************************************/
  /*
   * Show the App Summary Modal
   */
  appSummaryShow() {

    let appSummary = {
      template_id: 0,
      custom_value: ['']
    };

    if (!this.bulkEdit) {
      const appSummaryTemplateId = this.appSummaryData.appSummary.template_id;
      const customValue = this.appSummaryData.appSummary.custom_value;
      appSummary = {
        template_id: appSummaryTemplateId,
        custom_value: customValue
      };
    }
    this.appSummaryData.appSummary = appSummary;
    if (this.appSummaryModal instanceof AppSummaryModalComponent) {
      this.appSummaryModal.show(this.itemDetail.itemId, appSummary, this.bulkEdit);
    }
  }

  /*
   * Fires when the Application Summary is completed.
   * It is bound to the event onAppSummaryCompleted fired from the App Summary Modal
   * The descriptions html template sets the binding.
   */
  onAppSummaryCompleted(appSummary: any) {
    this.appSummaryData.selectedAppSummary = appSummary;
    _.delay(() => {
      if (this.confirmAppSummaryChangeModal instanceof SimpleConfirmModalComponent) {
        this.confirmAppSummaryChangeModal.show();
      }
    }, 200);
  }

  /*
   *  Fires when the Application Summary confirmation Modal is confirmed Yes.
   *  it is bound to the event secondaryBtnClick on the Confirmation Modal
   *  The descriptons html template sets the binding
   */
  onAppSummaryChangeConfirm() {
    this.updateAppSummary(this.appSummaryData.selectedAppSummary);
    this.appSummaryData.selectedAppSummary = null;
  }

  /*
   *  Fires when the Application Summary confirmation Modal is confirmed No.
   *  it is bound to the event primaryBtnClick on the Confirmation Modal
   *  The descriptons html template sets the binding
   */
  onAppSummaryChangeCancel() {
      const appSummaryTemplateId = _.get(this.appSummaryData.selectedAppSummary, 'template_id', 0);
      let customValue = '';
      if (appSummaryTemplateId === 0) {
        customValue = _.get(this.appSummaryData.selectedAppSummary, 'app_summary');
      } else {
        if (!this.bulkEdit) {
          customValue = _.get(this.itemDetail, 'descriptions.ASM.EN', '');
        }
      }
      const appSummary = {
        template_id: appSummaryTemplateId,
        custom_value: customValue
      };
    if (this.appSummaryModal instanceof AppSummaryModalComponent) {
      this.appSummaryModal.show(this.itemDetail.itemId, appSummary, this.bulkEdit);
    }
    this.appSummaryData.selectedAppSummary = null;
  }

  /*
   * Updates all of the necessary fields when the changing of an App Summary is Confirmed.
   */
  updateAppSummary(appSummary: any) {
    this.appSummaryData.appSummary.template_id = appSummary.template_id;
    if (appSummary.template_id === 0 ) {
      this.appSummaryData.appSummary.custom_value = appSummary.app_summary;
    }
    this.appSummary = appSummary.app_summary;
    this.appSummaryTemplateId = appSummary.template_id;
    this.markAsDirty();
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  // replaced parent
  public getSegmentFormData(): any {
    return this.segmentForm.value;
  }

  // replaced parent
  public getSegmentStoredBulkData(): any {
    return this.prepareBulkData(this.getSegmentFormData());
  }

  isLoopingField(item: any): boolean {
    return (item.type === 'list');
  }

  getLoopingformArray(fieldMap: string, language: string): UntypedFormArray {
    return this.segmentForm.controls[language].get(fieldMap) as UntypedFormArray;
  }

  isHalfSize(fieldMap: string): boolean {
    return _.includes(halfSizeFields, fieldMap);
  }

  hasItemInScores(item: SegmentFieldItem): boolean {
    return _.has(this.segmentScores, `${item.item_name}.${this.selectedLanguage}`);
  }

  hasLoopingItemInScores(item: SegmentFieldItem, index: number): boolean {
    return _.has(this.segmentScores, `${item.item_name}.${this.selectedLanguage}.${index}`);
  }

  isLoopingFieldMasked(item: SegmentFieldItem, i: number): boolean {
    return this.hasScores ? (!this.hasItemInScores(item) && !this.hasLoopingItemInScores(item, i)) : false;
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  // replace parent method:
  protected setBulkData(): void {
    this.setData({
      descriptions: this.getBulkSegmentData(this.segmentName)
    });
  }

  // replace parent method:
  protected setData(itemDetail: any = {}): void {
    /*
     * Set the App Summary Template Id
     */
    if (this.itemDetail) {
      this.appSummaryData.appSummary.template_id  = _.get(this.itemDetail, 'app_summary.template_id', 0);
      const customValue: string | Array<string> = _.get(this.itemDetail, 'app_summary.custom_value', ['']);
      this.appSummaryData.appSummary.custom_value = _.isArray(customValue) ? customValue : [customValue];
    }
    this.segmentForm.addControl('appSummaryTemplateId', new UntypedFormControl(this.appSummaryData.appSummary.template_id));

    _.each(this.segmentData, (item: SegmentFieldItem) => {

      if (item.field_map) {
        const validators: Array<any> =  this.getFieldValidators(item);
        if (item.type === 'list') {

          this.buildLoopingFormArray(itemDetail, item, 'en', validators);
          this.buildLoopingFormArray(itemDetail, item, 'es', validators);
          this.buildLoopingFormArray(itemDetail, item, 'fr', validators);

        } else {
          _.each(this.languageItems, (language: any) => {
            const control = this.segmentForm.controls[language.code.toLowerCase()].get(item.field_map);
            control.setValue(_.get(itemDetail, item.item_name + `.${language.code}`, ''));
            this.updateControlValidators(control, validators);
          });
        }
      }
    });
  }

  protected updateControlsValidation(): void {
    _.each(this.segmentData, (item: any) => {
      _.each(this.languageItems, (language: any) => {
        const lang = language.code.toLowerCase();
        const formControl: any = this.segmentForm.get(lang).get(item.field_map);
        const validators = this.getFieldValidators(item);
        if (!formControl) { return; }
        if (item.type === 'list') {
          // FormArray
          _.each(formControl.controls, (control: AbstractControl) => {
            this.updateControlValidators(control, validators);
          });
        } else {
          // FormControl
          this.updateControlValidators(formControl, validators);
        }
      });
    });
  }

  // replaced parent method:
  protected getItemScore(item: SegmentFieldItem): any {
    return _.get(this.segmentScores, `${item.item_name}.${this.selectedLanguage}`);
  }

  protected getItemIndexScore(item: SegmentFieldItem, index: number): any {
    return _.get(this.segmentScores, `${item.item_name}.${this.selectedLanguage}.${index}`);
  }

  /*
    * $event: {item: SegmentFieldItem, langauge: string // EN, ES, FR}
   */
  protected handleScoreDetailsExpand($event: any): void {
    this.selectedLanguageItem = _.find(this.languageItems, ['code', $event.language]);
    this.ref.detectChanges();
    if (!this.editMode) { return; }
    _.defer(() => {
      const control = this.findControlByFieldMap($event.item.field_map);
      if (control) { control.focusControl($event.item.fieldMap); }
    });
  }

  // replaced parent
  protected getScorecardField(itemName: string): any {
    return super.getScorecardField(`${itemName}.${this.selectedLanguage}`);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private buildLoopingFormArray(itemDetail: any, item: SegmentFieldItem, language: string, validators: Array<any>) {
      const oData: any = _.get(itemDetail, item.item_name + '.' + language.toUpperCase());
      const formGroup: any = this.segmentForm.controls[language.toLowerCase()];
      let formArray: any = formGroup.get(item.field_map);
      // reset formArray if array already has data
      if (formArray.length) {
        formGroup.setControl(item.field_map, new UntypedFormArray([]));
        formArray = formGroup.get(item.field_map);
      }
      if (oData) {
        _.each(oData, (desc) => {
          formArray.push(new UntypedFormControl(desc,  validators));
        });
      } else {
        // always add one empty element to the controls array
        formArray.push(new UntypedFormControl('',  validators));
      }

  }

  private languageFormGroup(data: Array<any>): DescriptionModel {
    const descriptionModel: DescriptionModel = new DescriptionModel();
    _.each(data, (d: any) => {

      if (d.field_map) {
        let validators: Array<any> = [];

        validators = this.getFieldValidators(d);

        if (d.type === 'list') {
          _.set(descriptionModel, d.field_map, new UntypedFormArray([]));
        } else {
          _.set(descriptionModel, d.field_map, new UntypedFormControl(_.get(d, 'default_value', ''),  validators) );
        }
      }

    });

    return descriptionModel;
  }

  private focusOnLastLoopingControl(fieldMap: string): void {
    _.defer(() => {
      const controls: Array<DetailsControlComponent> = this.loopingControls
      .filter((detailsControl: DetailsControlComponent) => {
        return detailsControl.fieldMap === fieldMap;
      });
      controls.pop().focusControl();
    });
  }

  private prepareBulkData(data: any): any {
    const result = {};
    _.each(data, (value: any, key: string) => {
      const lang = key.toUpperCase();
      _.each(value, (_value, _key: string) => {
        _.set(result, `${_key.toUpperCase()}.${lang}`, _value);
      });
    });
    return result;
  }

  /*
    * Group segment fields
   */
  private separateSegmentFields(segmentData: Array<any>): void {
    const groups: Array<any> = [];
    _.each(fieldGroups, (group: Array<string>) => {
      const groupedFields: any = _.filter(segmentData, (item: any) => _.includes(group, item.field_map));
      groups.push(groupedFields);
    });
    this.fieldGroups = groups;
  }

  private isClickable(item: SegmentFieldItem) {
    if (_.includes(['descriptions.ASM'], item.item_name) && this.editMode) {
      if (item.item_name === 'descriptions.ASM' && this.selectedLanguage === 'EN') {
        this.appSummaryShow();
      }
    }
  }

  getItemValidationMaxLength(item: SegmentFieldItem): number | null {
    if (item.item_name === 'descriptions.ASM') {
      return null;
    }
    const rule: ValidationItem = item.validation[this.scorecardData?.validation_rule] || this.getItemValidation(item);
    return rule ? rule.maxLength : null;
  }
}
